/**
 * A deadline for one request: a signal that aborts once `ms` pass or the caller's own signal aborts.
 * It is an `AbortController` and a timer, not `AbortSignal.timeout` or `AbortSignal.any`: some of the
 * runtimes this app is served in lack those, and reading one there throws before the request is made.
 *
 * @param ms How long the request may take, body included.
 * @param callerSignal A signal of the caller's that cancels the request too.
 * @returns The signal for the request, and the cleanup the caller must run once it settles.
 */
export function deadline(
  ms: number,
  callerSignal?: AbortSignal,
): { signal: AbortSignal; done: () => void } {
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timer = setTimeout(abort, ms);
  if (callerSignal?.aborted) abort();
  callerSignal?.addEventListener('abort', abort, { once: true });
  return {
    signal: controller.signal,
    done: () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener('abort', abort);
    },
  };
}
